import { useId } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { profilePhoneTransferService } from '@/entities/ProfilePhoneTransfer/api/profilePhoneTransferService';
import { profilePhoneTransferFormToRequest } from '@/entities/ProfilePhoneTransfer/mappers';
import { profilePhoneTransferSchema, type ProfilePhoneTransferFormValues } from '@/entities/ProfilePhoneTransfer/model/schema';
import type { ProfilePhoneTransferTarget } from '@/entities/ProfilePhoneTransfer/model/types';
import { getErrorMessage } from '@/shared/lib/errors';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { Modal } from '@/shared/ui/Modal/Modal';
import { RequestError } from '@/shared/ui/RequestState';
import { createRHFFields } from '@/shared/ui/RHFFields';
import classes from './ProfilePhoneTransferModal.module.scss';

interface ProfilePhoneTransferModalProps {
    target: ProfilePhoneTransferTarget;
    currentPhone?: string | null;
    onHide: () => void;
    onChanged: () => void | Promise<void>;
}

const { PhoneField, TextareaField } = createRHFFields<ProfilePhoneTransferFormValues>();

/** Показывает форму переноса существующего профиля на другой телефон */
export const ProfilePhoneTransferModal = (
    { target, currentPhone, onHide, onChanged }: ProfilePhoneTransferModalProps
) => {
    const formId = useId();

    const form = useForm<ProfilePhoneTransferFormValues>({
        resolver: zodResolver(profilePhoneTransferSchema),
        defaultValues: {
            phone: '',
            comment: ''
        },
    });

    const busy = form.formState.isSubmitting;

    /** Переносит профиль и обновляет данные открытого раздела */
    const submit = async (values: ProfilePhoneTransferFormValues): Promise<void> => {
        form.clearErrors('root.server');

        try {
            await profilePhoneTransferService.transfer(target, profilePhoneTransferFormToRequest(values));
        } catch (error: unknown) {
            form.setError('root.server', { message: getErrorMessage(error, 'Не удалось перенести профиль') });

            return;
        }

        toast.success('Профиль перенесён на новый телефон');
        onHide();
        try {
            await onChanged();
        } catch {
            toast.error('Профиль перенесён. Не удалось обновить данные страницы');
        }
    };

    return <Modal
        visible
        title="Перенести профиль"
        size="md"
        closable={!busy}
        onHide={() => { if (!busy) onHide(); }}
        footer={<div className={classes.footer}>
            <ActionButton
                type="button"
                label="Отмена"
                color="secondary"
                disabled={busy}
                onClick={onHide}
            />
            <ActionButton
                type="submit"
                form={formId}
                label="Перенести"
                icon="pi pi-phone"
                loading={busy}
                disabled={busy}
            />
        </div>}
    >
        <FormProvider {...form}>
            <form
                id={formId}
                onSubmit={form.handleSubmit(submit)}
                className={classes.form}
                noValidate
            >
                <p>Укажите новый телефон {target.kind === 'client'
                    ? 'клиента'
                    : 'исполнителя'}. Профиль, заказы и связанные данные сохранятся.</p>
                {currentPhone &&
                    <p>Текущий телефон: {currentPhone}</p>
                }
                <PhoneField
                    name="phone"
                    label="Новый телефон"
                    isRequired
                    disabled={busy}
                />
                <TextareaField
                    name="comment"
                    label="Комментарий"
                    placeholder="Почему меняется телефон и кто запросил изменение"
                    rows={4}
                    maxLength={500}
                    isRequired
                    disabled={busy}
                />
                {form.formState.errors.root?.server?.message &&
                    <RequestError
                        message={form.formState.errors.root.server.message}
                        compact
                    />
                }
            </form>
        </FormProvider>
    </Modal>;
};
